'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import type { DocumentoPadrao, DocumentoPadraoArquivo, DocumentoPadraoEscopo, DocumentoPadraoFormInput, DocumentoPadraoParte, DocumentoPadraoProduto, DocumentoPadraoTipo, DocumentosPadraoContexto } from '@/features/documentosPadrao/types/documentosPadraoTypes';
import { DOCUMENTO_PADRAO_PRODUTO_LABEL, DOCUMENTO_PADRAO_PRODUTOS, DOCUMENTO_PADRAO_TIPO_LABEL, DOCUMENTO_PADRAO_TIPOS, nomeSugerido, usosEmAndamento } from '@/features/documentosPadrao/utils/documentosPadraoRegras';
import { Button } from '@/shared/components/ui/common/Button';
import { Drawer } from '@/shared/components/ui/common/Drawer';
import { DropdownMenu } from '@/shared/components/ui/common/DropdownMenu';
import { IconButton } from '@/shared/components/ui/common/IconButton';
import { RadioCardGroup } from '@/shared/components/ui/common/RadioCardGroup';
import SearchableSelect from '@/shared/components/ui/common/SearchableSelect';
import { Select } from '@/shared/components/ui/common/Select';
import { TextField } from '@/shared/components/ui/common/TextField';
import { ToggleGroup } from '@/shared/components/ui/common/ToggleGroup';
import { formatFileSize } from '@/shared/formatters';
import { extensaoDoNome } from '@/shared/utils/detectarFormato';
import * as SubframeCore from '@subframe/core';
import { FeatherAlertTriangle, FeatherBookOpen, FeatherCalendar, FeatherCheck, FeatherEdit3, FeatherFileText, FeatherInfinity, FeatherPlus, FeatherUploadCloud, FeatherX } from '@subframe/core';
const FORMATOS_ACEITOS = '.pdf,.doc,.docx,.png,.jpg,.jpeg';
const DEBOUNCE_BUSCA_PARTE_MS = 300;
const paraInputDate = (iso: string | null): string => iso ? iso.slice(0, 10) : '';

/** Só para exibir no card do arquivo. O que sobe é o `File` — o binário deixou de ser descartado. */
const resumoDoArquivo = (arquivo: File | DocumentoPadraoArquivo) => arquivo instanceof File ? {
  nomeArquivo: arquivo.name,
  tamanhoBytes: arquivo.size
} : {
  nomeArquivo: arquivo.nomeArquivo,
  tamanhoBytes: arquivo.tamanhoBytes
};
interface DocumentoPadraoFormDrawerProps {
  aberto: boolean;
  documento: DocumentoPadrao | null;
  contexto: DocumentosPadraoContexto;
  /** Partes do catálogo do escritório, buscadas no servidor conforme a pessoa digita. */
  partes: DocumentoPadraoParte[];
  buscandoPartes: boolean;
  salvando: boolean;
  onBuscarPartes: (busca: string) => void;
  onFechar: () => void;
  onSalvar: (input: DocumentoPadraoFormInput) => void;
}
export function DocumentoPadraoFormDrawer(t0) {
  const $ = _c(200);
  const {
    aberto,
    documento,
    contexto,
    partes,
    buscandoPartes,
    salvando,
    onBuscarPartes,
    onFechar,
    onSalvar
  } = t0;
  const inputArquivoRef = useRef(null);
  const [termoBuscaParte, setTermoBuscaParte] = useState("");
  const termoBuscaParteDebounced = useDebouncedValue(termoBuscaParte, DEBOUNCE_BUSCA_PARTE_MS);
  const buscarPartesRef = useRef(onBuscarPartes);
  const [arquivo, setArquivo] = useState(null);
  const [nome, setNome] = useState("");
  const [nomeEditadoManualmente, setNomeEditadoManualmente] = useState(false);
  const [tipo, setTipo] = useState("PROCURACAO");
  const [expira, setExpira] = useState(false);
  const [validoAte, setValidoAte] = useState("");
  const [parteCatalogoId, setParteCatalogoId] = useState("");
  const [escopo, setEscopo] = useState("ESCRITORIO");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [setoresIds, setSetoresIds] = useState(t1);
  const [produtos, setProdutos] = useState(DOCUMENTO_PADRAO_PRODUTOS);
  const [erroValidacao, setErroValidacao] = useState("");
  let t2;
  if ($[1] !== onBuscarPartes) {
    t2 = () => {
      buscarPartesRef.current = onBuscarPartes;
    };
    $[1] = onBuscarPartes;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t2);
  let t3;
  let t4;
  if ($[3] !== aberto || $[4] !== termoBuscaParteDebounced) {
    t3 = () => {
      if (!aberto) {
        return;
      }
      buscarPartesRef.current(termoBuscaParteDebounced);
    };
    t4 = [aberto, termoBuscaParteDebounced];
    $[3] = aberto;
    $[4] = termoBuscaParteDebounced;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== aberto || $[8] !== documento) {
    t5 = () => {
      if (!aberto) {
        return;
      }
      setArquivo(documento?.arquivo ?? null);
      setNome(documento?.nome ?? "");
      setNomeEditadoManualmente(Boolean(documento));
      setTipo(documento?.tipo ?? "PROCURACAO");
      setExpira(Boolean(documento?.validoAte));
      setValidoAte(paraInputDate(documento?.validoAte ?? null));
      setParteCatalogoId(documento?.parteVinculada?.id ?? "");
      setEscopo(documento?.escopo ?? "ESCRITORIO");
      setSetoresIds(documento?.setoresIds ?? []);
      setProdutos(documento?.produtos ?? DOCUMENTO_PADRAO_PRODUTOS);
      setErroValidacao("");
    };
    t6 = [aberto, documento];
    $[7] = aberto;
    $[8] = documento;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  const editando = Boolean(documento);
  let T0;
  let T1;
  let handleAlternarProduto;
  let handleSalvar;
  let podeSalvar;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t7;
  let t8;
  let t9;
  if ($[11] !== aberto || $[12] !== arquivo || $[13] !== buscandoPartes || $[14] !== contexto.setores || $[15] !== documento || $[16] !== editando || $[17] !== erroValidacao || $[18] !== escopo || $[19] !== expira || $[20] !== nome || $[21] !== nomeEditadoManualmente || $[22] !== onFechar || $[23] !== onSalvar || $[24] !== parteCatalogoId || $[25] !== partes || $[26] !== produtos || $[27] !== setoresIds || $[28] !== tipo || $[29] !== validoAte) {
    const resumoArquivo = arquivo ? resumoDoArquivo(arquivo) : null;
    let t25;
    if ($[53] !== documento || $[54] !== partes) {
      t25 = documento?.parteVinculada && !partes.some(parte => parte.id === documento.parteVinculada?.id) ? [documento.parteVinculada, ...partes] : partes;
      $[53] = documento;
      $[54] = partes;
      $[55] = t25;
    } else {
      t25 = $[55];
    }
    const opcoesDeParte = t25;
    let t26;
    if ($[56] !== setoresIds) {
      t26 = setor => !setoresIds.includes(setor.id);
      $[56] = setoresIds;
      $[57] = t26;
    } else {
      t26 = $[57];
    }
    const setoresDisponiveis = contexto.setores.filter(t26);
    let t27;
    if ($[58] !== documento) {
      t27 = documento ? usosEmAndamento(documento) : 0;
      $[58] = documento;
      $[59] = t27;
    } else {
      t27 = $[59];
    }
    const processosEmAndamento = t27;
    let t28;
    if ($[60] !== arquivo || $[61] !== escopo || $[62] !== expira || $[63] !== nome || $[64] !== produtos || $[65] !== setoresIds || $[66] !== validoAte) {
      t28 = nome.trim().length >= 3 && Boolean(arquivo) && produtos.length > 0 && (!expira || Boolean(validoAte)) && (escopo !== "SETORES" || setoresIds.length > 0);
      $[60] = arquivo;
      $[61] = escopo;
      $[62] = expira;
      $[63] = nome;
      $[64] = produtos;
      $[65] = setoresIds;
      $[66] = validoAte;
      $[67] = t28;
    } else {
      t28 = $[67];
    }
    podeSalvar = t28;
    let t29;
    if ($[68] !== nomeEditadoManualmente || $[69] !== partes) {
      t29 = (proximoTipo, proximaParteId) => {
        if (nomeEditadoManualmente) {
          return;
        }
        const parte_0 = partes.find(item => item.id === proximaParteId) ?? null;
        setNome(nomeSugerido(proximoTipo, parte_0));
      };
      $[68] = nomeEditadoManualmente;
      $[69] = partes;
      $[70] = t29;
    } else {
      t29 = $[70];
    }
    const aplicarNomeSugerido = t29;
    let t30;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = evento => {
        const selecionado = evento.target.files?.[0];
        if (!selecionado) {
          return;
        }
        setArquivo(selecionado);
        evento.target.value = "";
      };
      $[71] = t30;
    } else {
      t30 = $[71];
    }
    const handleSelecionarArquivo = t30;
    let t31;
    if ($[72] !== aplicarNomeSugerido || $[73] !== parteCatalogoId) {
      t31 = proximoTipo_0 => {
        setTipo(proximoTipo_0);
        aplicarNomeSugerido(proximoTipo_0, parteCatalogoId);
      };
      $[72] = aplicarNomeSugerido;
      $[73] = parteCatalogoId;
      $[74] = t31;
    } else {
      t31 = $[74];
    }
    const handleTrocarTipo = t31;
    let t32;
    if ($[75] !== aplicarNomeSugerido || $[76] !== tipo) {
      t32 = proximaParteId_0 => {
        setParteCatalogoId(proximaParteId_0);
        aplicarNomeSugerido(tipo, proximaParteId_0);
      };
      $[75] = aplicarNomeSugerido;
      $[76] = tipo;
      $[77] = t32;
    } else {
      t32 = $[77];
    }
    const handleTrocarParte = t32;
    let t33;
    if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = produto => {
        setProdutos(atuais => atuais.includes(produto) ? atuais.filter(item_0 => item_0 !== produto) : [...atuais, produto].sort(_temp));
      };
      $[78] = t33;
    } else {
      t33 = $[78];
    }
    handleAlternarProduto = t33;
    let t34;
    if ($[79] !== arquivo || $[80] !== escopo || $[81] !== expira || $[82] !== nome || $[83] !== onSalvar || $[84] !== parteCatalogoId || $[85] !== produtos || $[86] !== setoresIds || $[87] !== tipo || $[88] !== validoAte) {
      t34 = () => {
        if (!arquivo) {
          setErroValidacao("Selecione o arquivo do documento.");
          return;
        }
        if (nome.trim().length < 3) {
          setErroValidacao("Informe um nome com pelo menos 3 caracteres.");
          return;
        }
        if (escopo === "SETORES" && setoresIds.length === 0) {
          setErroValidacao("Selecione ao menos um setor que pode usar o documento.");
          return;
        }
        if (produtos.length === 0) {
          setErroValidacao("Marque ao menos um produto onde o documento pode ser usado.");
          return;
        }
        if (expira && !validoAte) {
          setErroValidacao("Informe a data de validade ou marque que o documento n\xE3o expira.");
          return;
        }
        setErroValidacao("");
        onSalvar({
          nome: nome.trim(),
          tipo,
          validoAte: expira ? validoAte : null,
          parteCatalogoId: parteCatalogoId || null,
          escopo,
          setoresIds,
          produtos,
          arquivo: arquivo instanceof File ? arquivo : null
        });
      };
      $[79] = arquivo;
      $[80] = escopo;
      $[81] = expira;
      $[82] = nome;
      $[83] = onSalvar;
      $[84] = parteCatalogoId;
      $[85] = produtos;
      $[86] = setoresIds;
      $[87] = tipo;
      $[88] = validoAte;
      $[89] = t34;
    } else {
      t34 = $[89];
    }
    handleSalvar = t34;
    T1 = Drawer;
    t23 = aberto;
    t24 = onFechar;
    T0 = Drawer.Content;
    t22 = "max-w-xl";
    t20 = "flex h-full w-full flex-col";
    let t35;
    if ($[90] !== editando) {
      t35 = <div className="bg-brand-50 text-brand-700 flex h-10 w-10 flex-none items-center justify-center rounded-full">{editando ? <FeatherEdit3 className="h-5 w-5" /> : <FeatherBookOpen className="h-5 w-5" />}</div>;
      $[90] = editando;
      $[91] = t35;
    } else {
      t35 = $[91];
    }
    const t36 = editando ? "Editar documento padr\xE3o" : "Novo documento padr\xE3o";
    let t37;
    if ($[92] !== t36) {
      t37 = <span className="text-heading-3 font-heading-3 text-default-font">{t36}</span>;
      $[92] = t36;
      $[93] = t37;
    } else {
      t37 = $[93];
    }
    const t38 = editando ? documento?.nome : "Fica dispon\xEDvel para reuso em todos os produtos";
    let t39;
    if ($[94] !== t38) {
      t39 = <span className="text-caption font-caption text-subtext-color">{t38}</span>;
      $[94] = t38;
      $[95] = t39;
    } else {
      t39 = $[95];
    }
    let t40;
    if ($[96] !== t37 || $[97] !== t39) {
      t40 = <div className="flex flex-col">{t37}{t39}</div>;
      $[96] = t37;
      $[97] = t39;
      $[98] = t40;
    } else {
      t40 = $[98];
    }
    let t41;
    if ($[99] !== t35 || $[100] !== t40) {
      t41 = <div className="flex items-center gap-3">{t35}{t40}</div>;
      $[99] = t35;
      $[100] = t40;
      $[101] = t41;
    } else {
      t41 = $[101];
    }
    let t42;
    if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
      t42 = <FeatherX />;
      $[102] = t42;
    } else {
      t42 = $[102];
    }
    let t43;
    if ($[103] !== onFechar) {
      t43 = <IconButton variant="neutral-tertiary" icon={t42} onClick={onFechar} />;
      $[103] = onFechar;
      $[104] = t43;
    } else {
      t43 = $[104];
    }
    if ($[105] !== t41 || $[106] !== t43) {
      t21 = <div className="flex flex-none items-center justify-between gap-4 border-b border-solid border-neutral-100 px-6 py-4">{t41}{t43}</div>;
      $[105] = t41;
      $[106] = t43;
      $[107] = t21;
    } else {
      t21 = $[107];
    }
    t11 = "flex grow flex-col gap-6 overflow-y-auto p-6";
    if ($[108] !== editando || $[109] !== processosEmAndamento) {
      t12 = editando && processosEmAndamento > 0 && <div className="border-warning-200 bg-warning-50 flex items-start gap-3 rounded-[12px] border border-solid p-3"><FeatherAlertTriangle className="text-warning-700 mt-0.5 h-4 w-4 flex-none" /><div className="flex flex-col gap-1"><span className="text-body-bold font-body-bold text-warning-800">{`Este documento está em uso em ${processosEmAndamento} ${processosEmAndamento === 1 ? "processo" : "processos"} em andamento`}</span><span className="text-caption font-caption text-warning-700">A alteração vale para os próximos usos. Processos já enviados mantêm o arquivo como foi protocolado.</span></div></div>;
      $[108] = editando;
      $[109] = processosEmAndamento;
      $[110] = t12;
    } else {
      t12 = $[110];
    }
    if ($[111] !== erroValidacao) {
      t13 = erroValidacao && <div className="border-error-200 bg-error-50 text-error-700 text-body font-body rounded-[8px] border border-solid p-3">{erroValidacao}</div>;
      $[111] = erroValidacao;
      $[112] = t13;
    } else {
      t13 = $[112];
    }
    if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <input ref={inputArquivoRef} type="file" accept={FORMATOS_ACEITOS} className="hidden" onChange={handleSelecionarArquivo} />;
      $[113] = t14;
    } else {
      t14 = $[113];
    }
    t15 = arquivo ? <div className={`flex items-center gap-3 rounded-[8px] border border-solid p-3 ${editando ? "border-neutral-200 bg-neutral-50" : "border-success-200 bg-success-50"}`}><div className={`flex h-9 w-9 flex-none items-center justify-center rounded-[8px] ${editando ? "text-brand-700 bg-white" : "bg-success-100 text-success-700"}`}>{editando ? <FeatherFileText className="h-[18px] w-[18px]" /> : <FeatherCheck className="h-[18px] w-[18px]" />}</div><div className="min-w-0 flex-1"><p className="text-body-bold font-body-bold text-default-font truncate">{resumoArquivo?.nomeArquivo}</p><p className="text-caption font-caption text-subtext-color">{editando ? `${formatFileSize(resumoArquivo?.tamanhoBytes ?? 0)} · v${documento?.versaoAtual} · use “Substituir arquivo” para trocar` : `${formatFileSize(resumoArquivo?.tamanhoBytes ?? 0)} · ${extensaoDoNome(resumoArquivo?.nomeArquivo ?? "").toUpperCase()}`}</p></div>{!editando && <Button variant="neutral-secondary" size="small" onClick={() => inputArquivoRef.current?.click()}>Trocar</Button>}</div> : <button type="button" onClick={() => inputArquivoRef.current?.click()} className="flex w-full flex-col items-center gap-2 rounded-[12px] border border-dashed border-neutral-300 bg-neutral-50 px-6 py-8 transition-colors hover:border-neutral-400"><FeatherUploadCloud className="text-subtext-color h-6 w-6" /><span className="text-body-bold font-body-bold text-default-font">Selecione o arquivo do documento</span><span className="text-caption font-caption text-subtext-color">PDF, DOC, DOCX, PNG ou JPG — máx 25MB</span></button>;
    let t44;
    if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
      t44 = evento_0 => {
        setNome(evento_0.target.value);
        setNomeEditadoManualmente(true);
      };
      $[114] = t44;
    } else {
      t44 = $[114];
    }
    if ($[115] !== nome) {
      t16 = <TextField className="w-full" label="Nome do documento *" helpText="Sugerimos o nome a partir do tipo + parte vinculada."><TextField.Input value={nome} placeholder={"Ex: Procura\xE7\xE3o \u2014 Mariana Duarte Lopes"} onChange={t44} /></TextField>;
      $[115] = nome;
      $[116] = t16;
    } else {
      t16 = $[116];
    }
    let t45;
    if ($[117] !== handleTrocarTipo) {
      t45 = valor => handleTrocarTipo(valor as DocumentoPadraoTipo);
      $[117] = handleTrocarTipo;
      $[118] = t45;
    } else {
      t45 = $[118];
    }
    let t46;
    if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
      t46 = DOCUMENTO_PADRAO_TIPOS.map(_temp2);
      $[119] = t46;
    } else {
      t46 = $[119];
    }
    if ($[120] !== t45 || $[121] !== tipo) {
      t17 = <Select className="w-full" label="Tipo *" value={tipo} onValueChange={t45}>{t46}</Select>;
      $[120] = t45;
      $[121] = tipo;
      $[122] = t17;
    } else {
      t17 = $[122];
    }
    let t47;
    if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = <span className="text-caption-bold font-caption-bold text-default-font">Validade *</span>;
      $[123] = t47;
    } else {
      t47 = $[123];
    }
    const t48 = expira ? "expira" : "nao-expira";
    let t49;
    if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
      t49 = valor_0 => {
        if (!valor_0) {
          return;
        }
        setExpira(valor_0 === "expira");
        if (valor_0 === "nao-expira") {
          setValidoAte("");
        }
      };
      $[124] = t49;
    } else {
      t49 = $[124];
    }
    let t50;
    if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
      t50 = <ToggleGroup.Item value="nao-expira" icon={<FeatherInfinity />}>Não expira</ToggleGroup.Item>;
      $[125] = t50;
    } else {
      t50 = $[125];
    }
    let t51;
    if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
      t51 = <ToggleGroup.Item value="expira" icon={<FeatherCalendar />}>Tem data de validade</ToggleGroup.Item>;
      $[126] = t51;
    } else {
      t51 = $[126];
    }
    let t52;
    if ($[127] !== t48) {
      t52 = <ToggleGroup value={t48} onValueChange={t49}>{t50}{t51}</ToggleGroup>;
      $[127] = t48;
      $[128] = t52;
    } else {
      t52 = $[128];
    }
    let t53;
    if ($[129] !== expira || $[130] !== validoAte) {
      t53 = expira ? <TextField className="mobile:w-full w-[220px]" label="" helpText="Avisamos 30 dias antes e paramos de sugerir depois do vencimento."><TextField.Input type="date" value={validoAte} onChange={evento_1 => setValidoAte(evento_1.target.value)} /></TextField> : <span className="text-caption font-caption text-subtext-color">O documento fica válido por tempo indeterminado e nunca sai das sugestões por vencimento.</span>;
      $[129] = expira;
      $[130] = validoAte;
      $[131] = t53;
    } else {
      t53 = $[131];
    }
    if ($[132] !== t52 || $[133] !== t53) {
      t18 = <div className="flex w-full flex-col gap-2">{t47}{t52}{t53}</div>;
      $[132] = t52;
      $[133] = t53;
      $[134] = t18;
    } else {
      t18 = $[134];
    }
    let t54;
    if ($[135] !== opcoesDeParte) {
      t54 = opcoesDeParte.map(_temp3);
      $[135] = opcoesDeParte;
      $[136] = t54;
    } else {
      t54 = $[136];
    }
    let t55;
    if ($[137] !== buscandoPartes || $[138] !== handleTrocarParte || $[139] !== parteCatalogoId || $[140] !== t54) {
      t55 = <SearchableSelect label="Vincular a uma parte (opcional)" placeholder="Buscar por nome, CPF ou CNPJ" value={parteCatalogoId} onChange={handleTrocarParte} onSearchChange={setTermoBuscaParte} isLoadingMore={buscandoPartes} options={t54} />;
      $[137] = buscandoPartes;
      $[138] = handleTrocarParte;
      $[139] = parteCatalogoId;
      $[140] = t54;
      $[141] = t55;
    } else {
      t55 = $[141];
    }
    let t56;
    if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
      t56 = <span className="text-caption font-caption text-subtext-color">É o vínculo que permite ao sistema sugerir este arquivo sozinho nos próximos processos dessa pessoa.</span>;
      $[142] = t56;
    } else {
      t56 = $[142];
    }
    if ($[143] !== t55) {
      t19 = <div className="flex w-full flex-col gap-1.5">{t55}{t56}</div>;
      $[143] = t55;
      $[144] = t19;
    } else {
      t19 = $[144];
    }
    t7 = "flex w-full flex-col gap-3 border-t border-solid border-neutral-100 pt-5";
    if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <span className="text-caption-bold font-caption-bold text-default-font">Quem pode usar *</span>;
      $[145] = t8;
    } else {
      t8 = $[145];
    }
    let t57;
    if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
      t57 = valor_1 => setEscopo(valor_1 as DocumentoPadraoEscopo);
      $[146] = t57;
    } else {
      t57 = $[146];
    }
    let t58;
    if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
      t58 = <RadioCardGroup.RadioCard value="ESCRITORIO"><div className="flex flex-col gap-0.5"><span className="text-body font-body text-default-font">Todo o escritório</span><span className="text-caption font-caption text-subtext-color">Qualquer membro do workspace pode anexar este documento.</span></div></RadioCardGroup.RadioCard>;
      $[147] = t58;
    } else {
      t58 = $[147];
    }
    let t59;
    if ($[148] === Symbol.for("react.memo_cache_sentinel")) {
      t59 = <RadioCardGroup.RadioCard value="SETORES"><div className="flex flex-col gap-0.5"><span className="text-body font-body text-default-font">Setores específicos</span><span className="text-caption font-caption text-subtext-color">Só quem pertence aos setores selecionados.</span></div></RadioCardGroup.RadioCard>;
      $[148] = t59;
    } else {
      t59 = $[148];
    }
    let t60;
    if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
      t60 = <RadioCardGroup.RadioCard value="PESSOAL"><div className="flex flex-col gap-0.5"><span className="text-body font-body text-default-font">Só eu</span><span className="text-caption font-caption text-subtext-color">Ninguém mais vê este documento.</span></div></RadioCardGroup.RadioCard>;
      $[149] = t60;
    } else {
      t60 = $[149];
    }
    if ($[150] !== escopo) {
      t9 = <RadioCardGroup value={escopo} onValueChange={t57}>{t58}{t59}{t60}</RadioCardGroup>;
      $[150] = escopo;
      $[151] = t9;
    } else {
      t9 = $[151];
    }
    t10 = escopo === "SETORES" && <div className="flex flex-wrap items-center gap-2">{setoresIds.map(setorId => {
        const setor_0 = contexto.setores.find(item_2 => item_2.id === setorId);
        if (!setor_0) {
          return null;
        }
        return <button key={setorId} type="button" onClick={() => setSetoresIds(atuais_0 => atuais_0.filter(item_3 => item_3 !== setorId))} className="border-brand-200 bg-brand-50 text-brand-800 text-caption font-caption flex h-7 items-center gap-1.5 rounded-full border border-solid px-2.5">{setor_0.nome}<FeatherX className="h-3 w-3" /></button>;
      })}{setoresDisponiveis.length > 0 && <SubframeCore.DropdownMenu.Root><SubframeCore.DropdownMenu.Trigger asChild={true}><button type="button" className="text-caption font-caption text-subtext-color flex h-7 items-center gap-1.5 rounded-full border border-dashed border-neutral-300 px-2.5"><FeatherPlus className="h-3 w-3" />Adicionar setor</button></SubframeCore.DropdownMenu.Trigger><SubframeCore.DropdownMenu.Portal><SubframeCore.DropdownMenu.Content side="bottom" align="start" sideOffset={4} asChild={true}><DropdownMenu className="z-[10010]">{setoresDisponiveis.map(setor_1 => <DropdownMenu.DropdownItem key={setor_1.id} onClick={() => setSetoresIds(atuais_1 => [...atuais_1, setor_1.id])}>{setor_1.nome}</DropdownMenu.DropdownItem>)}</DropdownMenu></SubframeCore.DropdownMenu.Content></SubframeCore.DropdownMenu.Portal></SubframeCore.DropdownMenu.Root>}</div>;
    $[11] = aberto;
    $[12] = arquivo;
    $[13] = buscandoPartes;
    $[14] = contexto.setores;
    $[15] = documento;
    $[16] = editando;
    $[17] = erroValidacao;
    $[18] = escopo;
    $[19] = expira;
    $[20] = nome;
    $[21] = nomeEditadoManualmente;
    $[22] = onFechar;
    $[23] = onSalvar;
    $[24] = parteCatalogoId;
    $[25] = partes;
    $[26] = produtos;
    $[27] = setoresIds;
    $[28] = tipo;
    $[29] = validoAte;
    $[30] = T0;
    $[31] = T1;
    $[32] = handleAlternarProduto;
    $[33] = handleSalvar;
    $[34] = podeSalvar;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
    $[47] = t22;
    $[48] = t23;
    $[49] = t24;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
  } else {
    T0 = $[30];
    T1 = $[31];
    handleAlternarProduto = $[32];
    handleSalvar = $[33];
    podeSalvar = $[34];
    t10 = $[35];
    t11 = $[36];
    t12 = $[37];
    t13 = $[38];
    t14 = $[39];
    t15 = $[40];
    t16 = $[41];
    t17 = $[42];
    t18 = $[43];
    t19 = $[44];
    t20 = $[45];
    t21 = $[46];
    t22 = $[47];
    t23 = $[48];
    t24 = $[49];
    t7 = $[50];
    t8 = $[51];
    t9 = $[52];
  }
  let t25;
  if ($[152] !== t10 || $[153] !== t7 || $[154] !== t8 || $[155] !== t9) {
    t25 = <div className={t7}>{t8}{t9}{t10}</div>;
    $[152] = t10;
    $[153] = t7;
    $[154] = t8;
    $[155] = t9;
    $[156] = t25;
  } else {
    t25 = $[156];
  }
  let t26;
  if ($[157] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="flex flex-col gap-0.5"><span className="text-caption-bold font-caption-bold text-default-font">Onde pode ser usado</span><span className="text-caption font-caption text-subtext-color">Deixe tudo marcado se o documento serve para qualquer produto.</span></div>;
    $[157] = t26;
  } else {
    t26 = $[157];
  }
  let t27;
  if ($[158] !== handleAlternarProduto || $[159] !== produtos) {
    t27 = DOCUMENTO_PADRAO_PRODUTOS.map(produto_0 => {
      const marcado = produtos.includes(produto_0);
      return <button key={produto_0} type="button" aria-pressed={marcado} onClick={() => handleAlternarProduto(produto_0)} className={`text-caption font-caption flex h-8 items-center gap-1.5 rounded-[8px] border border-solid px-3 transition-colors ${marcado ? "border-brand-600 bg-brand-50 text-brand-800 font-medium" : "text-subtext-color border-neutral-200 bg-transparent"}`}>{marcado && <FeatherCheck className="h-3.5 w-3.5" />}{DOCUMENTO_PADRAO_PRODUTO_LABEL[produto_0]}</button>;
    });
    $[158] = handleAlternarProduto;
    $[159] = produtos;
    $[160] = t27;
  } else {
    t27 = $[160];
  }
  let t28;
  if ($[161] !== t27) {
    t28 = <div className="flex w-full flex-col gap-3 border-t border-solid border-neutral-100 pt-5">{t26}<div className="flex flex-wrap gap-2">{t27}</div></div>;
    $[161] = t27;
    $[162] = t28;
  } else {
    t28 = $[162];
  }
  let t29;
  if ($[163] !== t11 || $[164] !== t12 || $[165] !== t13 || $[166] !== t14 || $[167] !== t15 || $[168] !== t16 || $[169] !== t17 || $[170] !== t18 || $[171] !== t19 || $[172] !== t25 || $[173] !== t28) {
    t29 = <div className={t11}>{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t25}{t28}</div>;
    $[163] = t11;
    $[164] = t12;
    $[165] = t13;
    $[166] = t14;
    $[167] = t15;
    $[168] = t16;
    $[169] = t17;
    $[170] = t18;
    $[171] = t19;
    $[172] = t25;
    $[173] = t28;
    $[174] = t29;
  } else {
    t29 = $[174];
  }
  let t30;
  if ($[175] !== onFechar || $[176] !== salvando) {
    t30 = <Button variant="neutral-secondary" onClick={onFechar} disabled={salvando}>Cancelar</Button>;
    $[175] = onFechar;
    $[176] = salvando;
    $[177] = t30;
  } else {
    t30 = $[177];
  }
  const t31 = !podeSalvar || salvando;
  const t32 = editando ? "Salvar altera\xE7\xF5es" : "Salvar documento";
  let t33;
  if ($[178] !== handleSalvar || $[179] !== salvando || $[180] !== t31 || $[181] !== t32) {
    t33 = <Button onClick={handleSalvar} disabled={t31} loading={salvando}>{t32}</Button>;
    $[178] = handleSalvar;
    $[179] = salvando;
    $[180] = t31;
    $[181] = t32;
    $[182] = t33;
  } else {
    t33 = $[182];
  }
  let t34;
  if ($[183] !== t30 || $[184] !== t33) {
    t34 = <div className="flex flex-none items-center justify-end gap-3 border-t border-solid border-neutral-100 bg-neutral-50 px-6 py-4">{t30}{t33}</div>;
    $[183] = t30;
    $[184] = t33;
    $[185] = t34;
  } else {
    t34 = $[185];
  }
  let t35;
  if ($[186] !== t20 || $[187] !== t21 || $[188] !== t29 || $[189] !== t34) {
    t35 = <div className={t20}>{t21}{t29}{t34}</div>;
    $[186] = t20;
    $[187] = t21;
    $[188] = t29;
    $[189] = t34;
    $[190] = t35;
  } else {
    t35 = $[190];
  }
  let t36;
  if ($[191] !== T0 || $[192] !== t22 || $[193] !== t35) {
    t36 = <T0 className={t22}>{t35}</T0>;
    $[191] = T0;
    $[192] = t22;
    $[193] = t35;
    $[194] = t36;
  } else {
    t36 = $[194];
  }
  let t37;
  if ($[195] !== T1 || $[196] !== t23 || $[197] !== t24 || $[198] !== t36) {
    t37 = <T1 open={t23} onOpenChange={t24}>{t36}</T1>;
    $[195] = T1;
    $[196] = t23;
    $[197] = t24;
    $[198] = t36;
    $[199] = t37;
  } else {
    t37 = $[199];
  }
  return t37;
}
function _temp3(parte_1) {
  return {
    value: parte_1.id,
    label: parte_1.nome,
    description: parte_1.cpfCnpj ?? ""
  };
}
function _temp2(item_1) {
  return <Select.Item key={item_1} value={item_1}>{DOCUMENTO_PADRAO_TIPO_LABEL[item_1]}</Select.Item>;
}
function _temp(a, b) {
  return DOCUMENTO_PADRAO_PRODUTOS.indexOf(a) - DOCUMENTO_PADRAO_PRODUTOS.indexOf(b);
}